import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { AuthProvider, I18nProvider, OperationProvider, installBrowserTelemetry, reportBrowserError } from "@geomanager/common";
import { createMessages, createRouter } from "./router";

import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { RegistryProvider } from "./moduleLoader.provider";
import { getRegistry } from "./moduleLoader";
import type { Registry } from "./moduleLoader.types";

installBrowserTelemetry();

function clearLegacyTableResultCache() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("table:lastResult:")) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Session storage can be unavailable in hardened browser modes.
  }
}

function BackofficeApp({ registry }: Readonly<{ registry: Registry }>) {
  const router = React.useMemo(() => createRouter(registry), [registry]);
  const i18nMessages = React.useMemo(() => createMessages(registry), [registry]);
  const operationEndpoints = React.useMemo(() => [
    "/api/Operations",
    ...(registry.config.modules.av ? ["/api/av/Operations"] : []),
    ...(registry.config.modules.le ? ["/api/le/Operations"] : []),
  ], [registry.config.modules.av, registry.config.modules.le]);

  return (
    <React.StrictMode>
      <RegistryProvider registry={registry}>
        <AuthProvider>
          <OperationProvider endpoints={operationEndpoints}>
            <I18nProvider locale="it" messages={i18nMessages}>
              <RouterProvider router={router} />
            </I18nProvider>
          </OperationProvider>
        </AuthProvider>
      </RegistryProvider>
    </React.StrictMode>
  );
}

function Bootstrap() {
  const [registry, setRegistry] = React.useState<Registry | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    clearLegacyTableResultCache();
    getRegistry()
      .then((value) => {
        if (!cancelled) setRegistry(value);
      })
      .catch((err) => {
        reportBrowserError(err, "bootstrap");
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          <h1 className="h4">Errore di avvio</h1>
          <p className="mb-0">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!registry) {
    return (
      <div className="container py-4">
        <div className="d-flex align-items-center gap-3">
          <div className="spinner-border" role="status" aria-label="Caricamento" />
          <span>Caricamento in corso...</span>
        </div>
      </div>
    );
  }

  return <BackofficeApp registry={registry} />;
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement, {
  onUncaughtError: (error) => reportBrowserError(error, "react"),
  onCaughtError: (error) => reportBrowserError(error, "react"),
}).render(<Bootstrap />);
